/*  eslint-disable camelcase */
import { annotateEventSentences, Annotation } from '@litlingo/react';
import { createSelector } from '@reduxjs/toolkit';
import {
  addBulkEnvelopeTagsRequest,
  addCommentEnvelopeRequest,
  addEnvelopeTagRequest,
  exportEnvelopesSearch,
  fetchAllEnvelopesRequest,
  fetchEnvelopeThreadRequest,
  fetchFiltersCountsRequest,
  fetchMoreLikeThisRequest,
  fetchSingleEnvelopeRequest,
  removeEnvelopeTagRequest,
  reprocessEnvelopeRequest,
  reviewAndContinueNextEnvelopeRequest,
  reviewEnvelopeRequest,
} from 'actions';
import { fetchCommentsRequest } from 'actions/envelopeActions';
import { SummaryTagType } from 'components/Envelope/SummaryTag';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { defaultReviewer } from 'constants/reviewSets';
import { startCase } from 'lodash';
import moment from 'moment';
import { GlobalState } from 'reducers';
import { getSingleEvent } from 'selectors/events';
import type {
  Action,
  API,
  Communication,
  CommunicationEnvelope,
  CommunicationEnvelopeEsDocs,
  CommunicationEnvelopeEsDocsAsIndexed,
  CommunicationEnvelopeSummary,
  EntityTag,
  EnvelopeAction,
  MModelMetric,
  ModelMetric,
  MoreLikeThisPoint,
  NormalizedResource,
  RenderedAction,
  Review,
  Selector,
  UUID,
} from 'types';
import {
  IGNORE_TYPES,
  MESSAGE_IGNORE_TYPES,
  REVIEW_IGNORE_TYPES,
  TERMINAL_ACTION_WITHIN_POLICY,
  TERMINAL_ACTIONS,
  TERMINAL_ACTIONS_OUT_OF_POLICY,
} from 'utils/activityLogLookup';
import { isFileCommunication } from 'utils/communications';
import languagesMapping from 'utils/languages';
import {
  addDatesToFiltersSearch,
  DataNode,
  getAllFieldsValuesWithLabels,
  lookAllForField,
  Tree,
  TreeFieldsValuesWithLabels,
  ValueNode,
} from 'utils/parserTree';
import { meetsRolesReqs } from 'utils/permissions';
import {
  getCustomer,
  getSurroundingContextIdentifiersIds,
  getSurroundingContextRulesIds,
  getUser,
} from './auth';
import getSelectedEnvelope from './envelopeReview';
import { getNavParamsByResource } from './nav';

export const getFetchAllEnvelopesLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(fetchAllEnvelopesRequest.toString());

export const addBulkEnvelopeTagsLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(addBulkEnvelopeTagsRequest.toString());

export const getFetchSingleEnvelopeLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(fetchSingleEnvelopeRequest.toString());

export const getAddCommentLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(addCommentEnvelopeRequest.toString());

export const getFiltersCountsLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(fetchFiltersCountsRequest.toString());

export const getFetchCommentsLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(fetchCommentsRequest.toString());

export const getEnvelopes: Selector<CommunicationEnvelopeSummary[]> = createSelector(
  [(state: GlobalState): GlobalState['envelopes']['summaries'] => state.envelopes.summaries],
  (summaries) => Object.values(summaries)
);

export const getEnvelope: Selector<CommunicationEnvelope, [UUID]> = (state, envelopeId) =>
  state.envelopes.envelopes[envelopeId] || null;

export const getEnvelopeSummary: Selector<CommunicationEnvelopeSummary, [UUID]> = (
  state,
  envelopeId
) => state.envelopes.summaries[envelopeId];

export const getEnvelopeThreadLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(fetchEnvelopeThreadRequest.toString());

export const getEnvelopeThread: Selector<CommunicationEnvelopeSummary[]> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['envelopeThread'] =>
      state.envelopes.envelopeThread,
  ],
  (threads) => Object.values(threads)
);

export const getThreadAttachments: Selector<Communication[], [string]> = createSelector(
  [getEnvelopeThread, (_state, envelopeId): string => envelopeId],
  (Threads, envelopeId) => {
    const thread = Threads.find((t) => t.envelope.uuid === envelopeId);
    if (!thread) return [];
    if (thread?.envelope?.communications) {
      return thread?.envelope?.communications.filter((c) => c.communication_type === 'attachment');
    }
    return [];
  }
);

export const getThreadUsers: Selector<string[], [string]> = createSelector(
  [getEnvelopeThread, (_state, envelopeId): string => envelopeId],
  (Threads, envelopeId) => {
    const thread = Threads.find((t) => t.envelope.uuid === envelopeId);
    const users = new Set<string>();

    if (!thread) {
      return [];
    }

    if (thread?.envelope?.created_by?.email) {
      users.add(thread?.envelope?.created_by?.email);
    }

    if (thread?.envelope?.communications) {
      thread?.envelope?.communications.forEach((c) => {
        if (c.recipients) {
          c.recipients.forEach((r) => {
            users.add(r.id);
          });
        }
      });
    }

    return Array.from(users);
  }
);

export const getThreadRecipients: Selector<string[], [string]> = createSelector(
  [getEnvelopeThread, (_state, envelopeId): string => envelopeId],
  (Threads, envelopeId) => {
    const thread = Threads.find((t) => t.envelope.uuid === envelopeId);
    const users = new Set<string>();

    if (!thread) {
      return [];
    }

    if (thread?.envelope?.communications) {
      thread?.envelope?.communications.forEach((c) => {
        if (c.recipients) {
          c.recipients.forEach((r) => {
            users.add(r.id);
          });
        }
      });
    }

    return Array.from(users);
  }
);

export const getThreadComments: Selector<EnvelopeAction[], [string]> = createSelector(
  [getEnvelopeThread, (_state, envelopeId): string => envelopeId],
  (Threads, envelopeId) => {
    const thread = Threads.find((t) => t.envelope.uuid === envelopeId);
    const comments: EnvelopeAction[] = [];

    if (!thread) {
      return [];
    }

    thread.envelope.actions?.forEach((a) => {
      if (a.type === 'comment') {
        comments.push(a);
      }
    });

    return comments;
  }
);

export const getEnvelopeThreadCount: Selector<number> = (state) =>
  state.envelopes.envelopeThreadCount;

export const getThreadHasHitsFromSelectedCampaign: Selector<boolean, [UUID]> = (
  state,
  envelopeId
) => {
  const { selectedCampaign, selectedRule, selectedReviewer } = state.envelopeReview;
  const shouldFilter = selectedReviewer !== defaultReviewer.value;

  if (selectedRule) return false;

  const envelopeEvents = state.envelopes?.envelopeThread[envelopeId]?.events;

  return envelopeEvents
    .filter((e) => e.tenant_name !== 'modeler' || !shouldFilter)
    .some((e) => e.campaign_uuid === selectedCampaign);
};

export const getThreadHasHitsFromSelectedRule: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const { selectedRule, selectedReviewer } = state.envelopeReview;
  const envelopeEvents = state.envelopes?.envelopeThread[envelopeId]?.events;
  const shouldFilter = selectedReviewer !== defaultReviewer.value;

  return envelopeEvents
    .filter((e) => e.tenant_name !== 'modeler' || !shouldFilter)
    .some((e) => e.rule_uuid === selectedRule?.uuid);
};

export const getThreadHasHitsFromSelected: Selector<boolean, [UUID]> = (state, envelopeId) =>
  getThreadHasHitsFromSelectedCampaign(state, envelopeId) ||
  getThreadHasHitsFromSelectedRule(state, envelopeId);

export const getThreadsWithMatches: Selector<CommunicationEnvelopeSummary[]> = (state) => {
  const envelopes = state.envelopes?.envelopeThread;
  const { selectedEnvelope } = state.envelopeReview;

  return Object.values(envelopes)
    .filter((env) => env.envelope.platform_thread_guid === selectedEnvelope?.platform_thread_guid)
    .filter((e) => e.events && e.events.length > 0);
};

export const getEnvelopesListIds: Selector<UUID[]> = (state) => state.envelopes.listIds;

export const getEnvelopesFlaggedTextListIds: Selector<UUID[]> = (state) =>
  state.envelopes.flaggedTextlistIds;

export const getAnnotatedSetencesFromSummary =
  (
    envelopeId: UUID,
    eventId: UUID
  ): Selector<ReturnType<typeof annotateEventSentences>['sentences'] | null> =>
  ({ envelopes }): ReturnType<typeof annotateEventSentences>['sentences'] | null => {
    const envelopeSummary = envelopes.summaries[envelopeId];
    const event = envelopeSummary.events.find((e) => e.uuid === eventId);

    if (event == null || event.annotations == null || event.sentences == null) {
      return null;
    }

    return annotateEventSentences(event.sentences, event.annotations).sentences;
  };

export const getSentencesFromSummary =
  (
    envelopeId: UUID,
    eventId: UUID
  ): Selector<ReturnType<typeof annotateEventSentences>['sentences'] | null> =>
  ({ envelopes }): ReturnType<typeof annotateEventSentences>['sentences'] | null => {
    const envelopeSummary = envelopes.summaries[envelopeId];
    const event = envelopeSummary.events.find((e) => e.uuid === eventId);

    if (event == null || event.sentences == null) {
      return null;
    }

    return annotateEventSentences(event.sentences, []).sentences;
  };

export const makeGetAnnotatedSetencesFromSummaryByCommunication = (): Selector<
  ReturnType<typeof annotateEventSentences>['sentences'] | null,
  [UUID, UUID, UUID]
> =>
  createSelector(
    [
      (state: GlobalState): GlobalState['envelopes']['summaries'] => state.envelopes.summaries,
      (_state: GlobalState, envelopeId): string => envelopeId,
      (_state: GlobalState, _envelopeId, communicationId): string => communicationId,
      (_state: GlobalState, _envelopeId, _communicationId, selectedRuleId): string =>
        selectedRuleId,
    ],
    (
      summaries,
      envelopeId,
      communicationId,
      selectedRuleId
    ): ReturnType<typeof annotateEventSentences>['sentences'] | null => {
      const envelopeSummary = summaries[envelopeId];
      const events = envelopeSummary?.events.filter(
        (e) => e.communication_uuid === communicationId
      );

      if (
        !events ||
        events.length === 0 ||
        events[0].annotations == null ||
        events[0].sentences == null
      ) {
        return null;
      }

      if (!selectedRuleId) {
        const sentences = events.reduce(
          (acc, e) => ({ ...acc, ...e.sentences }),
          {} as CommunicationEnvelopeSummary['events'][number]['sentences']
        );
        if (!sentences) return null;

        return annotateEventSentences(sentences, []).sentences;
      }

      const sentences = events.reduce(
        (acc, e) => (e.rule_uuid === selectedRuleId ? { ...acc, ...e.sentences } : acc),
        {} as CommunicationEnvelopeSummary['events'][number]['sentences']
      );
      if (!sentences) return null;

      let annotations: Annotation[] = [];

      events.forEach((e) => {
        if (e.rule_uuid === selectedRuleId && e.annotations) {
          annotations = [...annotations, ...e.annotations];
        }
      });

      const highlightedAnnotations = annotations.map((a) => ({
        ...a,
        highlight: true,
      }));

      if (highlightedAnnotations.length > 200) {
        return [];
      }

      return annotateEventSentences(sentences, highlightedAnnotations).sentences;
    }
  );

export const makeGetNoMatchSetencesFromSummaryByCommunication = (): Selector<
  ReturnType<typeof annotateEventSentences>['sentences'] | null,
  [UUID]
> =>
  createSelector(
    [
      (state: GlobalState): GlobalState['envelopes']['summaries'] => state.envelopes.summaries,
      (_state: GlobalState, envelopeId): string => envelopeId,
    ],
    (summaries, envelopeId) => {
      const envelopeSummary = summaries[envelopeId];
      const event = envelopeSummary?.noMatchEvent;

      if (!event || !event.sentences || Object.values(event.sentences).length === 0) {
        return null;
      }

      return annotateEventSentences(event.sentences, []).sentences;
    }
  );
export const getCommEnvelopeActions: Selector<RenderedAction[], [UUID]> = (state, envelopeId) => {
  const renderedActions = state.envelopes.envelopes[envelopeId]?.action_summary;
  const actions = renderedActions?.filter((action) => !IGNORE_TYPES.includes(action.type));
  if (!actions) {
    return [];
  }
  return actions.slice().sort((a, b) => {
    const dateA: Date = a.created_at ? new Date(a.created_at) : new Date();
    const dateB: Date = b.created_at ? new Date(b.created_at) : new Date();
    return dateB.getTime() - dateA.getTime();
  });
};

export const getCommEnvelopeMessageHistory: Selector<RenderedAction[], [UUID]> = (
  state,
  envelopeId
) => {
  const renderedActions = state.envelopes.envelopes[envelopeId]?.action_summary;
  const actions = renderedActions?.filter((action) => !MESSAGE_IGNORE_TYPES.includes(action.type));
  if (!actions) {
    return [];
  }
  return actions.slice().sort((a, b) => {
    const dateA: Date = a.created_at ? new Date(a.created_at) : new Date();
    const dateB: Date = b.created_at ? new Date(b.created_at) : new Date();
    return dateB.getTime() - dateA.getTime();
  });
};

export type GroupedRenderedActions = {
  user: { name: string; uuid: string };
  year: string;
  actions: RenderedAction[];
};

export const getCommEnvelopeReviewHistory: Selector<
  Record<string, GroupedRenderedActions[]>,
  [UUID]
> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['envelopes'] => state.envelopes.envelopes,
    (_state, envelopeId): string => envelopeId,
  ],
  (envelopes, envelopeId) => {
    // in minutes
    const SESSION_LEEWAY = 3;

    const renderedActions = envelopes[envelopeId]?.action_summary as RenderedAction[];
    const actions = renderedActions?.filter((action) => !REVIEW_IGNORE_TYPES.includes(action.type));

    if (!actions) {
      return {};
    }

    const groupedActions: Record<string, GroupedRenderedActions[]> = {};

    const sortedActions = actions.sort((a, b) => {
      const dateA: Date = a.created_at ? new Date(a.created_at) : new Date();
      const dateB: Date = b.created_at ? new Date(b.created_at) : new Date();
      return dateB.getTime() - dateA.getTime();
    });

    sortedActions.forEach((a) => {
      const year = moment(a.created_at).year().toString();
      groupedActions[year] = [];
    });

    sortedActions.forEach((a) => {
      const year = moment(a.created_at).year().toString();
      let foundSesion = false;

      groupedActions[year].forEach((sesion) => {
        if (sesion.user.uuid === a.author_uuid && !foundSesion) {
          const lastAction = sesion.actions[sesion.actions.length - 1];
          const lastDate = moment(lastAction.created_at);
          const curDate = moment(a.created_at);

          if (Math.abs(curDate.diff(lastDate, 'minutes')) <= SESSION_LEEWAY) {
            sesion.actions.push(a);
            foundSesion = true;
          }
        }
      });

      if (!foundSesion) {
        groupedActions[year] = [
          ...groupedActions[year],
          {
            user: { name: startCase(a.author) || '', uuid: a.author_uuid || '' },
            year,
            actions: [a],
          },
        ];
      }
    });

    return groupedActions;
  }
);

export const getEnvelopesTotalCount: Selector<number> = (state) => state.envelopes.count;
export const getEnvelopesAggs: Selector<
  | {
      [key: string]: {
        [key: string]: {
          count: {
            buckets: { key: string; doc_count: number }[];
          };
          doc_count: number;
        };
      };
    }
  | undefined
> = (state) => state.envelopes.aggs;

export const getPreviousSingleEnvelope: Selector<
  (CommunicationEnvelope & { page_id?: string }) | null,
  [UUID]
> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['summaries'] => state.envelopes.summaries,
    (state: GlobalState): GlobalState['envelopes']['listIds'] => state.envelopes.listIds,
    (_state: GlobalState, envelopeId: string): string => envelopeId,
  ],
  (summaries, listIds, envelopeId): (CommunicationEnvelope & { page_id?: string }) | null => {
    const currentIdx = listIds.indexOf(envelopeId);

    if (currentIdx === -1 || currentIdx === 0) {
      return null;
    }

    return {
      ...summaries[listIds[currentIdx - 1]].envelope,
      page_id: summaries[listIds[currentIdx - 1]].page_id,
    };
  }
);

export const getNextSingleEnvelope: Selector<
  (CommunicationEnvelope & { page_id?: string }) | null,
  [UUID]
> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['summaries'] => state.envelopes.summaries,
    (state: GlobalState): GlobalState['envelopes']['listIds'] => state.envelopes.listIds,
    (_state: GlobalState, envelopeId: string): string => envelopeId,
  ],
  (summaries, listIds, envelopeId): (CommunicationEnvelope & { page_id?: string }) | null => {
    const currentIdx = listIds.indexOf(envelopeId);

    if (currentIdx === -1 || currentIdx === listIds.length - 1) {
      return null;
    }

    return {
      ...summaries[listIds[currentIdx + 1]].envelope,
      page_id: summaries[listIds[currentIdx + 1]].page_id,
    };
  }
);

export const getSingleEnvelopeTags =
  (envelopeId: UUID): Selector<EntityTag[]> =>
  (state): EntityTag[] => {
    const envelopeSummary = getEnvelope(state, envelopeId);
    if (envelopeSummary == null || envelopeSummary.tags == null) {
      return [];
    }

    return envelopeSummary.tags;
  };

export const getIsRead: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const envelopeSummary = getEnvelopeSummary(state, envelopeId);
  if (envelopeSummary == null) {
    return true;
  }
  return envelopeSummary.envelope.is_read;
};

export const getReviewAndContinueLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(reviewAndContinueNextEnvelopeRequest.toString());

export const getReviewEnvelopeLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(reviewEnvelopeRequest.toString());

export const addEnvelopeTagLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(addEnvelopeTagRequest.toString());

export const removeEnvelopeTagLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(removeEnvelopeTagRequest.toString());

export const getMoreLikeThis: Selector<MoreLikeThisPoint[]> = (state) =>
  state.envelopes.moreLikeThis;

export const getMoreLikeThisLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(fetchMoreLikeThisRequest.toString());

export const hasWithinPolicy: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const envelope = getEnvelope(state, envelopeId);
  if (!envelope || !envelope.events) return false;
  let exists = false;
  envelope.events.forEach((envelopeEvent) => {
    const event = getSingleEvent(state, envelopeEvent.uuid);
    if (
      event &&
      event.actions &&
      event.actions.find((el) => el.type === TERMINAL_ACTION_WITHIN_POLICY)
    )
      exists = true;
  });

  return exists;
};

export const envelopeWithinPolicy: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const envelope = getEnvelope(state, envelopeId);
  if (!envelope || !envelope.action_summary) return false;
  let exists = false;
  envelope.action_summary.forEach((action) => {
    if (action.type === TERMINAL_ACTION_WITHIN_POLICY) {
      exists = true;
    }
  });

  return exists;
};

export const envelopeOutOfPolicy: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const envelope = getEnvelope(state, envelopeId);
  if (!envelope || !envelope.action_summary) return false;
  let exists = false;
  envelope.action_summary.forEach((action) => {
    if (TERMINAL_ACTIONS_OUT_OF_POLICY.includes(action.type)) {
      exists = true;
    }
  });

  return exists;
};

export const getWithinPolicyValue: Selector<Action['value'] | null, [UUID]> = (
  state,
  envelopeId
) => {
  const envelope = getEnvelope(state, envelopeId);
  if (!envelope || !envelope.events) return null;
  let payload = null;
  envelope.events.forEach((event) => {
    if (event.actions) {
      event.actions.forEach((action) => {
        if (action.type === TERMINAL_ACTION_WITHIN_POLICY) {
          payload = action.value;
        }
      });
    }
  });

  return payload;
};

export const getCommunicationFromEnvelope: Selector<Communication, [UUID]> = (state, commId) => {
  const envelopes = Object.values(state.envelopes.envelopes);
  let communication = {} as Communication;
  envelopes.forEach((envelope) => {
    const selected = envelope.communications?.find((element) => element.uuid === commId);
    if (selected) communication = selected;
  });

  return communication;
};

export const getEnvelopeEsDocs: Selector<CommunicationEnvelopeEsDocs> = (state) =>
  state.envelopes.esDocs;

export const getEnvelopeEsDocsAsIndexed: Selector<CommunicationEnvelopeEsDocsAsIndexed> = (state) =>
  state.envelopes.esDocsAsIndexed;

export const getExportEnvelopesSEarchLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(exportEnvelopesSearch.toString());

export const getReprocessEnvelopeLoading: Selector<boolean> = (state) =>
  state.envelopes.loading.includes(reprocessEnvelopeRequest.toString());

export const getFetchFlaggedTextLoading: Selector<boolean, [UUID]> = (state, envelopeId) =>
  state.envelopes.loading.includes(envelopeId);

export const getEnvelopeInFlaggedIds: Selector<boolean, [UUID]> = (state, envelopeId) =>
  state.envelopes.flaggedTextlistIds.includes(envelopeId);

export const getSingleEnvelopeComments: Selector<EnvelopeAction[], [UUID]> = createSelector(
  [
    (state): GlobalState['envelopes']['envelopes'] => state.envelopes.envelopes,
    (_state, envelopeId): string => envelopeId,
  ],
  (envelopes, envelopeId) => {
    let comments: EnvelopeAction[] = [];
    const rawActions = envelopes[envelopeId]?.actions;
    if (!rawActions) {
      return comments;
    }

    comments = rawActions
      .filter((item) => item.type === 'comment' && item.deleted_at === null)
      .sort((a, b) => {
        if (a.created_at == null || b.created_at == null) {
          return 0;
        }

        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB.getTime() - dateA.getTime();
      });

    return comments;
  }
);

export const getRecipientsFromEnvelope: Selector<Communication['recipients'], [UUID]> =
  createSelector(
    [
      (state): GlobalState['envelopes']['envelopes'] => state.envelopes.envelopes,
      (_state, envelopeId): string => envelopeId,
    ],
    (envelopes, envelopeId) => {
      const envelope = envelopes[envelopeId];
      if (!envelope) return [];
      const { communications } = envelope;

      const initialValue: Communication['recipients'] = [];

      if (communications) {
        const recipients = communications.reduce<Communication['recipients']>((rec, comm) => {
          if (comm.recipients) {
            const t = [...rec, ...comm.recipients];
            return t;
          }
          return rec;
        }, initialValue);

        return recipients;
      }
      return [];
    }
  );

export const getRecipientsFromCommunication: Selector<Communication['recipients']> = createSelector(
  [
    (state: GlobalState): GlobalState['communication']['selectedCommunication'] =>
      state.communication.selectedCommunication,
  ],
  (communication) => {
    if (!communication) return [];

    const { recipients } = communication;
    if (recipients) return recipients;

    return [];
  }
);

export const getFromRecipientsFromCommunication: Selector<Communication['recipients']> = (
  state
) => {
  const communication = state.communication.selectedCommunication;

  if (!communication) return [];

  const { recipients } = communication;

  if (recipients) return recipients.filter((r) => r.role === 'from');

  return [];
};

export const getToRecipientsFromCommunication: Selector<Communication['recipients']> =
  createSelector(
    [
      (state: GlobalState): GlobalState['communication']['selectedCommunication'] =>
        state.communication.selectedCommunication,
    ],
    (communication) => {
      if (!communication) return [];

      const { recipients } = communication;

      if (recipients) return recipients.filter((r) => r.role === 'to');

      return [];
    }
  );

export const getCcRecipientsFromCommunication: Selector<Communication['recipients']> =
  createSelector(
    [
      (state: GlobalState): GlobalState['communication']['selectedCommunication'] =>
        state.communication.selectedCommunication,
    ],
    (communication) => {
      if (!communication) return [];

      const { recipients } = communication;

      if (recipients) return recipients.filter((r) => r.role === 'cc');

      return [];
    }
  );

export const getBccRecipientsFromCommunication: Selector<Communication['recipients']> =
  createSelector(
    [
      (state: GlobalState): GlobalState['communication']['selectedCommunication'] =>
        state.communication.selectedCommunication,
    ],
    (communication) => {
      if (!communication) return [];

      const { recipients } = communication;

      if (recipients) return recipients.filter((r) => r.role === 'bcc');

      return [];
    }
  );

export const getLastReview: Selector<Review | null, [UUID]> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['envelopes'] => state.envelopes.envelopes,
    (_state: GlobalState, envelopeId): string => envelopeId,
  ],
  (envelopes, envelopeId) => {
    const envelope = envelopes[envelopeId];
    if (!envelope) return null;

    const reviews: Review[] = envelope.reviews ? [...envelope.reviews] : [];

    if (!reviews || reviews.length === 0) return null;

    reviews.sort((a, b) => (a.updated_at && b.updated_at && a.updated_at > b.updated_at ? -1 : 1));

    return reviews[0];
  }
);

export const getEnvelopeTeamsNames: Selector<string[], [UUID]> = createSelector(
  [getEnvelope],
  (envelope) => {
    if (envelope && envelope.created_by && envelope.created_by.teams) {
      return envelope.created_by?.teams?.map((t) => t.name);
    }

    return [];
  }
);

export const getEnvelopeIsInternal: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const envelope = state.envelopes.envelopes[envelopeId];

  if (envelope) return envelope.internal;

  return false;
};

export const getEnvelopeIsPartOfThread: Selector<boolean> = (state) => {
  const threads = state.envelopes.envelopeThread;

  if (Object.values(threads).length > 1) return true;
  return false;
};

export const getRecipientsByPlatform = (
  comm: Communication,
  envelope: CommunicationEnvelope
): Communication['recipients'] => {
  if (comm.recipients && comm.recipients.length > 1)
    if (comm.platform === 'gmail') {
      return comm.recipients.filter(
        (r) => r.role !== 'from' && r.id !== envelope.created_by?.email
      );
    }

  if (comm.platform === 'o365') {
    return comm.recipients.filter(
      (r) => r.role !== 'from' && r.id !== envelope.created_by?.email_index
    );
  }

  if (comm.platform === 'slack') {
    return comm.recipients.filter(
      (r) => r.role !== 'from' && r.id !== envelope.created_by?.slack_id
    );
  }

  if (comm.platform === 'o365_teams') {
    return comm.recipients.filter(
      (r) => r.role !== 'from' && r.display_name !== envelope.created_by?.name
    );
  }

  return (comm.recipients || []).filter((r) => r.role !== 'from');
};

const isSlackChannel = (envelope: CommunicationEnvelope): boolean => {
  if (envelope.platform === 'slack') {
    return envelope.communication_type === 'channel';
  }

  return false;
};

const isTeamsChannel = (envelope: CommunicationEnvelope): boolean => {
  if (envelope.platform === 'o365_teams') {
    return envelope.communications?.some((c) => c.communication_type === 'channel') || false;
  }
  return false;
};

export const getEnvelopeHasSeveralRecipients: Selector<boolean, [UUID]> = (state, envelopeId) => {
  const envelope = state.envelopes.envelopes[envelopeId];
  if (envelope && envelope.communications) {
    const recipients = envelope.communications.reduce<Communication['recipients']>((rec, com) => {
      const comRecipients = getRecipientsByPlatform(com, envelope);
      return [...rec, ...comRecipients];
    }, []);

    const uniqueRecipients = recipients.filter(
      (r, idx, arr) => arr.findIndex((re) => re.id === r.id) === idx
    );

    return uniqueRecipients.length > 1 || isSlackChannel(envelope) || isTeamsChannel(envelope);
  }
  return false;
};

export const getCommunicationHasAttachments: Selector<boolean> = (state) => {
  const communication = state.communication.selectedCommunication;

  if (communication?.attachments) {
    return communication.attachments.length > 0;
  }

  return false;
};

export const getSurroundingContextRulesNames: Selector<string[]> = createSelector(
  [
    getSurroundingContextRulesIds,
    getSelectedEnvelope,
    (state: GlobalState): GlobalState['envelopeReview']['selectedReviewer'] =>
      state.envelopeReview.selectedReviewer,
  ],
  (surroundingContextRulesIds, envelope, selectedReviewer) => {
    const surroundingContextRulesNames: string[] = [];
    const shouldFilter = selectedReviewer !== defaultReviewer.value;

    if (surroundingContextRulesIds) {
      surroundingContextRulesIds.forEach((sRule) => {
        const event = envelope?.events
          ?.filter((e) => e.tenant_name !== 'modeler' || !shouldFilter)
          .find((e) => e.rule?.uuid === sRule || e.rule_uuid === sRule);
        if (event && event.rule) {
          surroundingContextRulesNames.push(event.rule?.name);
        }
      });
    }

    return surroundingContextRulesNames;
  }
);

export const getSurroundingContextIdentifiersNames: Selector<string[]> = createSelector(
  [
    getSurroundingContextIdentifiersIds,
    getSelectedEnvelope,
    (state: GlobalState): GlobalState['events']['events'] => state.events.events,
    (state: GlobalState): GlobalState['envelopeReview']['selectedReviewer'] =>
      state.envelopeReview.selectedReviewer,
  ],
  (surroundingContextIdentifiersIds, envelope, events, selectedReviewer) => {
    const shouldFilter = selectedReviewer !== defaultReviewer.value;

    const filteredEvents = Object.values(events)
      .filter((e) => e.tenant_name !== 'modeler' || !shouldFilter)
      .filter((e) => envelope?.events?.some((ev) => ev.uuid === e.uuid));

    const surroundingContextIdentifiersNames: string[] = [];

    if (surroundingContextIdentifiersIds) {
      surroundingContextIdentifiersIds.forEach((sIdentifier) => {
        const event = filteredEvents?.find((e) =>
          e.annotations?.some((a) => a.annotator_uuid === sIdentifier)
        );
        const annotation = event?.annotations?.find((a) => a.annotator_uuid === sIdentifier);

        if (event && annotation) {
          surroundingContextIdentifiersNames.push(annotation.name);
        }
      });
    }

    return surroundingContextIdentifiersNames;
  }
);

export const getEnvelopeMetadata: Selector<string[], [UUID]> = createSelector(
  [
    getUser,
    getSelectedEnvelope,
    getEnvelopeTeamsNames,
    getEnvelopeIsPartOfThread,
    getEnvelopeHasSeveralRecipients,
    getCommunicationHasAttachments,
    (state: GlobalState): GlobalState['events']['events'] => state.events.events,
    (_state, envelopeId): UUID => envelopeId,
    (state: GlobalState): GlobalState['envelopeReview']['selectedReviewer'] =>
      state.envelopeReview.selectedReviewer,
  ],
  (
    user,
    envelope,
    teams,
    isPartOfThread,
    hasSeveralRecipients,
    hasAttachments,
    events,
    envelopeId,
    selectedReviewer
  ) => {
    const isFile = envelope?.communications?.[0]
      ? isFileCommunication(envelope.communications[0])
      : false;
    const context: string[] = [];

    if (teams.length > 0) {
      const text = teams.reduce((acc, t, idx, arr) => {
        if (idx === arr.length - 1) return `${acc} ${t}`;
        if (idx === arr.length - 2) return `${acc} ${t} and`;
        if (arr.length > 1) return `${acc} ${t},`;
        return `${acc} ${t}`;
      }, 'Sender is in ');

      context.push(text);
    }

    // TODO: fix the datathat drives this
    // if (getEnvelopeIsInternal(state, envelopeId)) {
    //   context.push('Sent internally');
    // } else {
    //   context.push('Sent externally');
    // }

    if (isPartOfThread) {
      context.push(isFile ? 'Multipart File' : 'Part of thread');
    }

    if (hasSeveralRecipients) {
      context.push('Between individual and group');
    } else if (envelope?.communication_type === 'email') {
      context.push('Between two individuals');
    }

    if (hasAttachments) {
      context.push('Has attachments');
    }

    const uniqueLlmOutput = new Set();

    const shouldFilter = selectedReviewer !== defaultReviewer.value;

    Object.values(events)
      .filter((e) => e.tenant_name !== 'modeler' || !shouldFilter)
      .forEach((e) => {
        if (e.communication_envelope_uuid === envelopeId && e.annotations) {
          e.annotations.forEach((anno) => {
            // @ts-ignore
            if (anno.description && !anno.model_kind?.includes('no_context')) {
              // @ts-ignore
              uniqueLlmOutput.add(anno.description);
            }
          });
        }
      });

    if (meetsRolesReqs(user.roles, ['super-admin'])) {
      uniqueLlmOutput.forEach((e) => {
        // @ts-ignore
        context.push(e);
      });
    }

    return context;
  }
);

export type Custodian = { name: string; folders: string[] };

export const getCustodians: Selector<Custodian[]> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopeReview']['selectedEnvelope'] =>
      state.envelopeReview.selectedEnvelope,
  ],
  (envelope) => {
    const custodians: Custodian[] = [];

    if (envelope) {
      const { communications } = envelope;

      communications?.forEach((communication) => {
        if (
          communication &&
          communication.meta_data &&
          communication.meta_data.custodian &&
          communication.meta_data.folder
        ) {
          const { custodian, folder } = communication.meta_data;

          const cIndex = custodians.findIndex((c) => c.name === custodian);

          if (cIndex !== -1) {
            if (!custodians[cIndex].folders.includes(folder)) {
              custodians[cIndex].folders.push(folder);
            }
          } else {
            const newCustodian: Custodian = { name: custodian, folders: [folder] };
            custodians.push(newCustodian);
          }
        }
      });
    }

    return custodians;
  }
);

export const getContextModels: Selector<string[]> = createSelector(
  [getSurroundingContextRulesNames, getSurroundingContextIdentifiersNames],
  (surroundingContextRulesNames, surroundingContextIdentifiersNames) => {
    const context: string[] = [];

    surroundingContextRulesNames.forEach((ruleName) => {
      context.push(ruleName);
    });

    surroundingContextIdentifiersNames.forEach((identifierName) => {
      context.push(identifierName);
    });

    return context;
  }
);

export const getTree: Selector<Tree> = (state) => state.envelopes.filtersTree;

export const getFiltersSearchString: Selector<string> = (state) => {
  const envelopeParams = getNavParamsByResource(resourceQueryParamName.envelopes)(state);
  const actualTree = getTree(state);

  let filtersSearch = envelopeParams.filters_search || '';

  if (
    (envelopeParams.created_after || envelopeParams.created_before) &&
    !envelopeParams.days_from_oor
  ) {
    const fs = addDatesToFiltersSearch(envelopeParams, actualTree);
    if (fs) {
      filtersSearch = fs;
    }
  }
  return filtersSearch as string;
};

export const getSelectedFilters: Selector<ValueNode[]> = (state) => state.envelopes.selectedFilters;
export const getSelectedFields: Selector<DataNode[]> = (state) => state.envelopes.selectedFields;

export const getRequestTree: Selector<boolean> = (state) => state.envelopes.requestTreeFilters;

export const getAllFilterValuesFromTreeWithLabels: Selector<TreeFieldsValuesWithLabels> =
  createSelector(
    [(state: GlobalState): GlobalState['envelopes']['filtersTree'] => state.envelopes.filtersTree],
    (filtersTree) => getAllFieldsValuesWithLabels(filtersTree)
  );

export const getFilterValuesFromTree: Selector<string[], [string]> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['filtersTree'] => state.envelopes.filtersTree,
    (_, field): string => field,
  ],
  (filtersTree, field) => lookAllForField(filtersTree, field)
);

export const makeGetFilterValuesFromTree = (): Selector<string[], [string]> =>
  createSelector(
    [
      (state: GlobalState): GlobalState['envelopes']['filtersTree'] => state.envelopes.filtersTree,
      (_, field): string => field,
    ],
    (filtersTree, field) => lookAllForField(filtersTree, field)
  );

export const getSummaryTagFromSummaries: Selector<SummaryTagType, [UUID]> = createSelector(
  [getEnvelopeSummary],
  (summary) => {
    if (!summary) return 'none';
    const { envelope, key_actions } = summary;
    if (key_actions?.length === 0) return 'none';

    if (key_actions.includes('message_deleted') || key_actions.includes('retroactive_block')) {
      return 'deleted';
    }

    if (
      !Object.keys(TERMINAL_ACTIONS).some((a) => key_actions.includes(a)) &&
      envelope.integration_type === 'app' &&
      !envelope.communications?.some((c) => c.meta_data.folder_id === 'sentitems')
    ) {
      // return 'abandoned';
      return 'none';
    }

    if (key_actions.includes('sent_within_policy')) return 'reduced-all';

    if (key_actions.includes('send_anyways')) return 'high-risk';

    return 'none';
  }
);

export const getExperiments: Selector<MModelMetric[]> = (state) =>
  state.envelopes.modelMetrics || [];

export const getNewModelMetric: Selector<ModelMetric> = (state) =>
  state.envelopes.newModelMetric || null;

export const getTeamsContextSummaries: Selector<
  NormalizedResource<CommunicationEnvelopeSummary>
> = (state) => state.envelopes.teamsContextSummaries;

export const getFiltersCounts: Selector<Record<string, { count: number; uuid: string }[]>> = (
  state
) => state.envelopes.filtersCounts;

export const getLanguages: Selector<{ uuid: string; label: string; count: number }[]> =
  createSelector(
    [
      getCustomer,
      (state: GlobalState): GlobalState['envelopes']['filtersCounts'] =>
        state.envelopes.filtersCounts,
    ],
    (customer, counts) => {
      const languagesCounts = counts.languages;

      // @ts-ignore
      const allowedTranslations = customer?.config?.allowed_translations;
      // Filter out languages with a count of 0
      // Build a map of language code to count for efficient lookup
      const languageCountMap = (languagesCounts || []).reduce<Record<string, number>>((acc, c) => {
        // @ts-ignore
        acc[c.code] = c.count;
        return acc;
      }, {});

      const filteredLanguages = languagesMapping.filter((language) => {
        // @ts-ignore
        const languageCount = languageCountMap[language.value] || 0;
        return languageCount > 0;
      });
      const languagesWithCounts = filteredLanguages.map((language) => ({
        uuid: language.value,
        label: language.label,
        // @ts-ignore
        count: languageCountMap[language.value] || 0,
      }));

      if (allowedTranslations && !allowedTranslations.includes('*')) {
        return languagesWithCounts.filter((l) => allowedTranslations.includes(l.uuid));
      }

      return languagesWithCounts;
    }
  );

export const getEnvelopeRecipientsAggs: Selector<
  { recipient: string; higherRiskCount: number; afterHoursCount: number; offRadarCount: number }[]
> = (state) => state.envelopes.envelopeRecipientsAggs || [];

export const getLlmScores: Selector<API.FilterOptions.LlmScores | null> = (state) =>
  state.envelopes.promptScores;
